import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Delete from '@mui/icons-material/Delete';
import type { DashboardComponent, DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import {
  Alert,
  Button,
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogHeader,
  DialogTitle,
  TabNavigation,
} from 'components/ui';
import type { TabNavigationItem } from 'components/ui';
import { COMPONENT_TYPE_LABELS } from 'pages/Intelligence/SummaryDashboard/components/DashboardGrid/dashboardComponent.helpers';
import { PrismThemeProvider } from 'theme';
import AddComponentMenu from './components/AddComponentMenu';
import DashboardComponentList from './components/DashboardComponentList';
import DashboardFieldControl from './components/DashboardFieldControl';
import {
  addComponent,
  deleteComponent,
  moveComponent,
  pruneDraftForSave,
  readGridColumns,
  replaceComponent,
  seedDraft,
  setGridColumns,
} from './dashboardDraft';
import { validateDraft } from './dashboardDraft.validation';
import {
  buildComponentSections,
  buildDashboardSections,
  EMPTY_FIELD_AVAILABILITY,
} from './dashboardEditFields';
import { hasDashboardIssues } from './dashboardEditFields.types';
import type { DashboardEditSection, DashboardIssues } from './dashboardEditFields.types';
import type { SummaryDashboardEditDialogProps } from './SummaryDashboardEditDialog.props';
import {
  SummaryDashboardEditDialogBody,
  SummaryDashboardEditDialogEmpty,
  SummaryDashboardEditDialogIssueList,
  SummaryDashboardEditDialogRow,
  SummaryDashboardEditDialogRowControl,
  SummaryDashboardEditDialogRowHelper,
  SummaryDashboardEditDialogRowLabel,
  SummaryDashboardEditDialogRows,
  SummaryDashboardEditDialogSection,
  SummaryDashboardEditDialogSectionHeader,
  SummaryDashboardEditDialogSectionTitle,
  SummaryDashboardEditDialogTabs,
} from './SummaryDashboardEditDialog.styles';
import { SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS } from './SummaryDashboardEditDialog.testIds';

const DASHBOARD_TAB_VALUE = 'dashboard';

const EMPTY_DASHBOARD_MESSAGE =
  'This dashboard has no components yet. Add one below, or describe what you want to see in the prompt box on the tab.';

const SummaryDashboardEditDialog = ({
  dashboardDefinition = null,
  definitionId,
  fieldAvailability = EMPTY_FIELD_AVAILABILITY,
  existingNames,
  initialComponentId = null,
  isSaving = false,
  onClose,
  onSave,
  open,
  saveErrors = null,
  summaryName,
}: SummaryDashboardEditDialogProps) => {
  const [activeTab, setActiveTab] = useState<string>(DASHBOARD_TAB_VALUE);
  const [draft, setDraft] = useState<DashboardDefinition>(() => seedDraft(dashboardDefinition));
  const [name, setName] = useState(summaryName);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // Seeded once per opening rather than tracking `dashboardDefinition`: a prompt-bar revision can
  // land a refetch while the dialog is open, and re-seeding on it would silently discard the edits
  // in progress. A session ref keeps the dependency array honest without an exhaustive-deps escape.
  const seededSessionRef = useRef<string | null>(null);

  useEffect(() => {
    const session = open && definitionId !== null ? String(definitionId) : null;

    if (session === null || seededSessionRef.current === session) {
      seededSessionRef.current = session;
      return;
    }

    seededSessionRef.current = session;
    const seeded = seedDraft(dashboardDefinition);

    setDraft(seeded);
    setName(summaryName);
    // seedDraft keeps every id it is given and only mints one for a component that has none, so an
    // id read off a rendered tile still matches here. A component the stored payload never gave an
    // id falls through to the Dashboard tab rather than selecting a freshly minted one at random.
    setActiveTab(
      initialComponentId && seeded.components.some(component => component.id === initialComponentId)
        ? initialComponentId
        : DASHBOARD_TAB_VALUE
    );
    setHasAttemptedSave(false);
  }, [dashboardDefinition, definitionId, initialComponentId, open, summaryName]);

  const {components} = draft;
  const gridColumns = readGridColumns(draft);

  const validationIssues = useMemo(
    () => validateDraft({ definition: draft, existingNames, name }),
    [draft, existingNames, name]
  );

  // Client-side problems only surface after the first save attempt, so opening a dialog on an
  // AI-authored dashboard does not greet the user with a wall of red. Server errors always show.
  const visibleIssues: DashboardIssues | null = saveErrors ?? (hasAttemptedSave ? validationIssues : null);

  const handleComponentChange = useCallback(
    (index: number, next: DashboardComponent) =>
      setDraft(current => replaceComponent(current, index, next)),
    []
  );

  // Computed from the current draft rather than inside the setDraft updater: an updater must stay
  // pure, and under StrictMode it runs twice — which would mint two ids and select the discarded one.
  const handleAddComponent = useCallback(
    (type: DashboardComponent['type']) => {
      const { definition, id } = addComponent(draft, type);

      setDraft(definition);
      setActiveTab(id);
    },
    [draft]
  );

  const handleDeleteComponent = useCallback((id: string) => {
    setDraft(current => deleteComponent(current, id));
    setActiveTab(DASHBOARD_TAB_VALUE);
  }, []);

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => setDraft(current => moveComponent(current, fromIndex, toIndex)),
    []
  );

  const activeIndex = components.findIndex(component => component.id === activeTab);

  const tabItems = useMemo<TabNavigationItem[]>(
    () => [
      {
        label: 'Dashboard',
        value: DASHBOARD_TAB_VALUE,
        badge: visibleIssues?.general.length ? '!' : undefined,
      },
      ...components.map((component, index) => ({
        label: component.display?.title?.trim() || COMPONENT_TYPE_LABELS[component.type],
        value: component.id ?? `${component.type}-${index}`,
        // Marks the tabs a save failure came from, so nobody is stranded on the Dashboard tab
        // reading an error about components[3].
        badge: visibleIssues?.byComponentIndex.has(index) ? '!' : undefined,
      })),
    ],
    [components, visibleIssues]
  );

  const sections = useMemo<DashboardEditSection[]>(() => {
    if (activeTab === DASHBOARD_TAB_VALUE) {
      return buildDashboardSections({
        definition: draft,
        name,
        onLayoutColumnsChange: columns => setDraft(current => setGridColumns(current, columns)),
        onNameChange: setName,
      });
    }

    return activeIndex === -1
      ? []
      : buildComponentSections(
        components[activeIndex],
        gridColumns,
        next => handleComponentChange(activeIndex, next),
        fieldAvailability
      );
  }, [
    activeIndex,
    activeTab,
    components,
    draft,
    fieldAvailability,
    gridColumns,
    handleComponentChange,
    name,
  ]);

  const activeIssues =
    activeTab === DASHBOARD_TAB_VALUE
      ? (visibleIssues?.general ?? [])
      : (visibleIssues?.byComponentIndex.get(activeIndex) ?? []);

  const otherTabIssueCount =
    (visibleIssues
      ? visibleIssues.byComponentIndex.size + (visibleIssues.general.length ? 1 : 0)
      : 0) - (activeIssues.length ? 1 : 0);

  const handleSave = () => {
    setHasAttemptedSave(true);

    if (hasDashboardIssues(validationIssues) || isSaving) {
      return;
    }

    onSave(name.trim(), pruneDraftForSave(draft));
  };

  const shouldShowEmptyState = activeTab === DASHBOARD_TAB_VALUE && components.length === 0;

  // The Dialog family reads Prism-only tokens (palette.neutral, shape.radius) and the summary
  // workspace is not inside a Prism provider, so wrap here rather than at the call site.
  return (
    <PrismThemeProvider>
      <Dialog
        data-testid={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.dialog}
        fullWidth
        maxWidth="md"
        onClose={isSaving ? undefined : onClose}
        open={open}>
        <DialogHeader>
          <DialogTitle>Edit Summary</DialogTitle>
          <DialogCloseButton
            aria-label="Close edit summary dialog"
            data-testid={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.closeButton}
            disabled={isSaving}
            onClick={onClose}
            size="small">
            <DialogCloseIcon />
          </DialogCloseButton>
        </DialogHeader>
        <SummaryDashboardEditDialogTabs>
          <TabNavigation
            actions={
              <AddComponentMenu
                disabled={isSaving}
                label="Add"
                onAdd={handleAddComponent}
                testId={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.addComponentButton}
              />
            }
            ariaLabel="Dashboard components"
            items={tabItems}
            onChange={setActiveTab}
            showScrollButtons
            testId={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.tabs}
            value={activeTab}
          />
        </SummaryDashboardEditDialogTabs>
        <DialogContent>
          <SummaryDashboardEditDialogBody>
            {activeIssues.length > 0 && (
              <Alert
                data-testid={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.errorAlert}
                visualVariant="inlineError">
                <SummaryDashboardEditDialogIssueList>
                  {activeIssues.map(issue => (
                    <li key={issue}>{issue}</li>
                  ))}
                </SummaryDashboardEditDialogIssueList>
                {otherTabIssueCount > 0 && (
                  <SummaryDashboardEditDialogRowHelper>
                    {`${otherTabIssueCount} other tab${otherTabIssueCount === 1 ? '' : 's'} need attention — marked with !`}
                  </SummaryDashboardEditDialogRowHelper>
                )}
              </Alert>
            )}

            {sections.map(section => (
              <SummaryDashboardEditDialogSection
                data-testid={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.section(section.title)}
                key={section.title}>
                <SummaryDashboardEditDialogSectionTitle>{section.title}</SummaryDashboardEditDialogSectionTitle>
                <SummaryDashboardEditDialogRows>
                  {section.fields.map(field => (
                    <SummaryDashboardEditDialogRow key={field.key}>
                      <SummaryDashboardEditDialogRowLabel>{field.label}</SummaryDashboardEditDialogRowLabel>
                      <SummaryDashboardEditDialogRowControl>
                        <DashboardFieldControl
                          disabled={isSaving}
                          field={field}
                          testId={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.field(field.key)}
                        />
                        {field.helperText && (
                          <SummaryDashboardEditDialogRowHelper>{field.helperText}</SummaryDashboardEditDialogRowHelper>
                        )}
                      </SummaryDashboardEditDialogRowControl>
                    </SummaryDashboardEditDialogRow>
                  ))}
                </SummaryDashboardEditDialogRows>
              </SummaryDashboardEditDialogSection>
            ))}

            {activeTab === DASHBOARD_TAB_VALUE && (
              <SummaryDashboardEditDialogSection
                data-testid={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.section('Components')}>
                <SummaryDashboardEditDialogSectionHeader>
                  <SummaryDashboardEditDialogSectionTitle>Components</SummaryDashboardEditDialogSectionTitle>
                  <AddComponentMenu disabled={isSaving} onAdd={handleAddComponent} />
                </SummaryDashboardEditDialogSectionHeader>
                {shouldShowEmptyState ? (
                  <SummaryDashboardEditDialogEmpty
                    data-testid={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.emptyState}>
                    {EMPTY_DASHBOARD_MESSAGE}
                  </SummaryDashboardEditDialogEmpty>
                ) : (
                  <DashboardComponentList
                    components={components}
                    disabled={isSaving}
                    onDelete={handleDeleteComponent}
                    onReorder={handleReorder}
                    onSelect={setActiveTab}
                    testIds={{
                      deleteButton: SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.componentDeleteButton,
                      row: SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.componentRow,
                    }}
                  />
                )}
                <SummaryDashboardEditDialogRowHelper>
                  Drag to reorder. Tiles re-flow left to right across the grid in this order.
                </SummaryDashboardEditDialogRowHelper>
              </SummaryDashboardEditDialogSection>
            )}

            {activeIndex !== -1 && (
              <div>
                <Button
                  data-testid={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.deleteComponentButton}
                  disabled={isSaving}
                  onClick={() => handleDeleteComponent(components[activeIndex].id ?? '')}
                  sizeVariant="compact"
                  startIcon={<Delete fontSize="inherit" />}
                  visualVariant="danger">
                  Delete component
                </Button>
              </div>
            )}
          </SummaryDashboardEditDialogBody>
        </DialogContent>
        <DialogActionFooter
          actionButtonTestId={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.saveButton}
          actionLabel="Save"
          cancelButtonTestId={SUMMARY_DASHBOARD_EDIT_DIALOG_TEST_IDS.cancelButton}
          isActionDisabled={isSaving || (hasAttemptedSave && hasDashboardIssues(validationIssues))}
          isActionLoading={isSaving}
          isCancelDisabled={isSaving}
          onAction={handleSave}
          onCancel={onClose}
        />
      </Dialog>
    </PrismThemeProvider>
  );
};

export default SummaryDashboardEditDialog;
